import React, { useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { Chart, registerables } from 'chart.js';
import { ChartLine } from 'lucide-react';
import { EmptyState } from '../common';
import { formatPriceName } from '../../utils/priceName';
import { GRID_COLOR, TICK_COLOR, FONT_FAMILY, SERIES_PALETTE } from './elements/chartTheme';

Chart.register(...registerables);

// Brand-first palette (design-system series order); cycles if there are more series than colors.
const PALETTE = SERIES_PALETTE;

function hexToRgba(hex, a) {
    const m = String(hex).replace('#', '');
    const r = parseInt(m.slice(0, 2), 16);
    const g = parseInt(m.slice(2, 4), 16);
    const b = parseInt(m.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export default function PriceTrendChart({ series = [], weeks = [], onLineClick }) {
    const ref = useRef(null);
    const clickRef = useRef(onLineClick);
    clickRef.current = onLineClick;

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas || !series.length || !weeks.length) return undefined;
        if (canvas.chart) canvas.chart.destroy();

        const labels = weeks.map((w) => dayjs(w).format('DD/MM/YY'));
        const datasets = series.map((s, i) => {
            const byWeek = new Map(s.points.map((p) => [p.week, p.avgUsd]));
            const color = PALETTE[i % PALETTE.length];
            return {
                label: formatPriceName(s),
                data: weeks.map((w) => (byWeek.has(w) ? byWeek.get(w) : null)),
                _baseColor: color,
                borderColor: color,
                backgroundColor: color,
                borderWidth: 2,
                tension: 0.25,
                pointRadius: 2,
                pointHoverRadius: 5,
                spanGaps: true
            };
        });

        // Spotlight: hovering a line emphasizes it and fades the rest so overlapping
        // lines are easy to tell apart. Only redraw when the hovered line changes.
        let lastHover = -2;
        const applySpotlight = (chart, hovered) => {
            if (hovered === lastHover) return;
            lastHover = hovered;
            chart.data.datasets.forEach((ds, i) => {
                if (hovered < 0) {
                    ds.borderColor = ds._baseColor;
                    ds.borderWidth = 2;
                    ds.pointRadius = 2;
                    ds.order = 0;
                } else if (i === hovered) {
                    ds.borderColor = ds._baseColor;
                    ds.borderWidth = 3.5;
                    ds.pointRadius = 3;
                    ds.order = 1000; // higher order draws last → on top
                } else {
                    ds.borderColor = hexToRgba(ds._baseColor, 0.1);
                    ds.borderWidth = 1;
                    ds.pointRadius = 0;
                    ds.order = 0;
                }
            });
            chart.update('none');
        };

        canvas.chart = new Chart(canvas, {
            type: 'line',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'nearest', intersect: false },
                onHover: (evt, elements, chart) => {
                    // Only spotlight while the cursor is inside the plotting area (not over the
                    // legend, axes, or margins). Leaving the canvas is handled by mouseleave below.
                    const area = chart.chartArea;
                    const inside =
                        area && evt.x >= area.left && evt.x <= area.right && evt.y >= area.top && evt.y <= area.bottom;
                    const hovered = inside && elements.length ? elements[0].datasetIndex : -1;
                    if (evt?.native?.target) evt.native.target.style.cursor = hovered >= 0 ? 'pointer' : 'default';
                    applySpotlight(chart, hovered);
                },
                onClick: (evt, elements) => {
                    if (!elements.length || !clickRef.current) return;
                    const s = series[elements[0].datasetIndex];
                    if (s) clickRef.current(s);
                },
                plugins: {
                    legend: {
                        position: 'bottom',
                        labels: { boxWidth: 10, boxHeight: 10, color: TICK_COLOR, font: { size: 10, family: FONT_FAMILY }, padding: 8 }
                    },
                    tooltip: {
                        callbacks: {
                            label: (item) => `${item.dataset.label}: $${Number(item.parsed.y).toFixed(2)}`
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: false,
                        title: { display: true, text: 'USD', color: TICK_COLOR, font: { family: FONT_FAMILY } },
                        grid: { color: GRID_COLOR },
                        border: { display: false },
                        ticks: { color: TICK_COLOR, font: { family: FONT_FAMILY }, callback: (v) => `$${Number(v).toFixed(2)}` }
                    },
                    x: {
                        ticks: {
                            maxRotation: 90,
                            minRotation: 45,
                            autoSkip: true,
                            maxTicksLimit: 30,
                            color: TICK_COLOR,
                            font: { size: 9, family: FONT_FAMILY }
                        },
                        grid: { display: false },
                        border: { color: GRID_COLOR }
                    }
                }
            }
        });

        // onHover doesn't fire once the cursor leaves the canvas, so clear the spotlight here.
        const handleLeave = () => {
            if (canvas.chart) applySpotlight(canvas.chart, -1);
        };
        canvas.addEventListener('mouseleave', handleLeave);

        return () => {
            canvas.removeEventListener('mouseleave', handleLeave);
            canvas.chart?.destroy();
        };
    }, [series, weeks]);

    if (!series.length || !weeks.length) {
        return (
            <EmptyState
                icon={<ChartLine />}
                title="Sin datos para los filtros actuales"
                description="Amplía el rango de fechas o quita algún filtro."
            />
        );
    }

    // Give each week room; scroll horizontally when the period is long.
    const minWidth = Math.max(720, weeks.length * 40);
    return (
        <div className="overflow-x-auto overflow-y-hidden">
            <div style={{ width: `max(100%, ${minWidth}px)`, height: 640 }}>
                <canvas ref={ref} />
            </div>
        </div>
    );
}
